import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Printer, Hourglass, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MTopBar, ComandaBar } from "@/components/mobile/MTopBar";
import { MPage, Footer, Loading, cardCls, Chip, blueBtn } from "@/components/mobile/parts";
import { ModeBar, SplitBox, ItemsPick, MethodGrid, AmountBox, payPlan } from "@/components/mobile/PayPanel";
import { useComanda } from "@/hooks/useMobileData";
import { useMobile } from "@/context/MobileContext";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canDo, errMsg, PAY_LABELS, activeMethods } from "@/lib/api";
import { activeItems } from "@/lib/mobile";
import { printDoc, printErr } from "@/lib/btprint";
import { cn } from "@/lib/utils";

const Row = ({ l, v, cls }) => <div className={cn("flex justify-between", cls)}><span>{l}</span><span className="font-mono">{v}</span></div>;

function Totals({ c, hideItems }) {
  return (
    <div className={cn(cardCls, "space-y-1 p-4 text-sm")} data-testid="m-close-totals">
      {!hideItems && <>{activeItems(c).map((i) => <Row key={i.id} l={`${i.qty}x ${i.name}`} v={brl(i.total)} cls="text-slate-600" />)}<div className="my-2 border-t border-dashed" /></>}
      <Row l="Subtotal" v={brl(c.subtotal)} />
      {c.discount > 0 && <Row l="Desconto" v={`- ${brl(c.discount)}`} cls="text-red-600" />}
      {c.service_fee > 0 && <Row l="Taxa de serviço" v={brl(c.service_fee)} />}
      {c.surcharge > 0 && <Row l="Acréscimo" v={brl(c.surcharge)} />}
      {c.paid > 0 && <Row l="Já pago" v={`- ${brl(c.paid)}`} cls="text-emerald-600" />}
      <div className="flex items-end justify-between border-t pt-2"><span className="text-slate-500">A pagar</span><span className="font-display text-3xl font-bold" data-testid="m-close-remaining">{brl(c.remaining)}</span></div>
    </div>
  );
}

function DiscountBox({ c, reload }) {
  const { session } = useAuth();
  const fee = session.tenant.settings.service_fee_pct;
  const [v, setV] = useState("");
  const [ask, setAsk] = useState(false);
  const [pend, setPend] = useState([]);
  const can = canDo(session.user, "apply_discount");
  const loadPend = () => api.get(`/approvals?comanda_id=${c.id}`).then((r) => setPend(r.data.filter((a) => a.type === "discount"))).catch(() => {});
  useEffect(() => { loadPend(); }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const amount = Number(String(v).replace(",", ".")) || 0;
  const adjust = (patch) => api.post(`/comandas/${c.id}/adjust`, { discount: c.discount, surcharge: c.surcharge, apply_service_fee: !!c.apply_service_fee, ...patch });
  const apply = async () => {
    try { await adjust({ discount: amount }); toast.success("Desconto aplicado"); setV(""); reload(); }
    catch (e) { if (e.response?.status === 403) setAsk(true); toast.error(errMsg(e)); }
  };
  const request = async () => {
    try { await api.post("/approvals", { type: "discount", comanda_id: c.id, amount, reason: "Desconto no fechamento" }); toast.success("Pedido de desconto enviado ao gerente"); setV(""); setAsk(false); loadPend(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleFee = async (on) => { try { await adjust({ apply_service_fee: on }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={cn(cardCls, "space-y-3 p-4")} data-testid="m-discount-box">
      {fee > 0 && <label className="flex items-center justify-between text-sm font-medium">Taxa de serviço ({fee}%)<Switch checked={!!c.apply_service_fee} onCheckedChange={toggleFee} data-testid="m-service-fee-switch" /></label>}
      <div className="flex gap-2">
        <Input value={v} onChange={(e) => { setV(e.target.value); setAsk(false); }} inputMode="decimal" placeholder="Desconto em R$" className="h-11 bg-white text-base" data-testid="m-discount-input" />
        {can && !ask ? <Button onClick={apply} disabled={!amount} variant="outline" className="h-11" data-testid="m-discount-apply-btn">Aplicar</Button>
          : <Button onClick={request} disabled={!amount} className={cn("h-11", blueBtn)} data-testid="m-discount-request-btn">Pedir ao gerente</Button>}
      </div>
      {c.discount > 0 && <button onClick={() => adjust({ discount: 0 }).then(reload).catch((e) => toast.error(errMsg(e)))} className="text-xs font-semibold text-red-600" data-testid="m-discount-remove-btn">Remover desconto de {brl(c.discount)}</button>}
      {pend.filter((a) => a.status === "pending").map((a) => <Chip key={a.id} tone="amber" testId="m-discount-pending"><Hourglass className="h-3 w-3" />{a.description} · aguardando gerente</Chip>)}
    </div>
  );
}

function Done({ sale }) {
  const nav = useNavigate();
  const { session } = useAuth();
  const reprint = () => printDoc(session, "sale", `2ª via · Venda #${sale.number}`, { sale }).catch((e) => toast.error(printErr(e)));
  return (
    <MPage>
      <div className={cn(cardCls, "space-y-3 p-6 text-center")} data-testid="m-close-done">
        <CheckCircle2 className="mx-auto h-14 w-14 text-[#16a34a]" />
        <p className="font-display text-2xl font-bold">Venda #{sale.number} concluída</p>
        <p className="font-mono text-3xl font-bold">{brl(sale.total)}</p>
        <p className="text-sm text-slate-500">{sale.payments.map((p) => `${PAY_LABELS[p.method]} ${brl(p.amount)}`).join(" + ")}</p>
        {sale.change > 0 && <p className="text-lg font-semibold text-emerald-700" data-testid="m-close-change">Troco: {brl(sale.change)}</p>}
      </div>
      <Button variant="outline" className="h-12 w-full" onClick={reprint} data-testid="m-close-reprint-btn"><Printer className="mr-2 h-5 w-5" />Reimprimir comprovante</Button>
      <Button className={cn("h-12 w-full", blueBtn)} onClick={() => nav("/m/pdv", { replace: true })} data-testid="m-close-back-btn">Voltar ao PDV</Button>
    </MPage>
  );
}

function PrintAsk({ ask, onClose }) {
  const { session } = useAuth();
  const print = () => { printDoc(session, "partial", `Pagamento parcial · Comanda #${ask.partial.number}`, ask).catch((e) => toast.error(printErr(e))); onClose(); };
  return (
    <Dialog open={!!ask} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="m-partial-print-dialog">
        <DialogHeader><DialogTitle>Imprimir comprovante?</DialogTitle><DialogDescription>Comprovante do pagamento parcial de {ask && brl(ask.partial.amount)}.</DialogDescription></DialogHeader>
        <DialogFooter className="flex-row gap-2">
          <Button variant="outline" className="h-12 flex-1" onClick={onClose} data-testid="m-partial-print-no">Não</Button>
          <Button className={cn("h-12 flex-1", blueBtn)} onClick={print} data-testid="m-partial-print-yes"><Printer className="mr-2 h-4 w-4" />Imprimir</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const partialPayload = (c, after, plan, method, user) => ({ partial: {
  number: c.number, table_number: c.table_number, customer_name: c.customer_name, at: new Date().toISOString(), label: plan.label, user,
  items: plan.itemIds.length ? plan.items.map(({ name, qty, total }) => ({ name, qty, total })) : [],
  total: c.total, paid_before: c.paid, method, amount: plan.pay, received: plan.got, change: plan.change, remaining: after.remaining,
} });

function usePayState(session) {
  const methods = activeMethods(session).filter((m) => m !== "mercadopago");
  const [mode, setMode] = useState("toda");
  const [split, setSplit] = useState("iguais");
  const [people, setPeople] = useState(2);
  const [share, setShare] = useState("");
  const [sel, setSel] = useState([]);
  const [method, setMethod] = useState(methods.includes("dinheiro") ? "dinheiro" : methods[0]);
  const [recv, setRecv] = useState("");
  return { methods, mode, setMode, split, setSplit, people, setPeople, share, setShare, sel, setSel, method, setMethod, recv, setRecv };
}

export default function MClose() {
  const { id } = useParams();
  const { c, load, cid } = useComanda(id, 0);
  const { setCart, pendingFor } = useMobile();
  const [sale, setSale] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);
  const { session } = useAuth();
  const s = usePayState(session);
  if (!c) return <><MTopBar title="Fechamento" back={`/m/comanda/${id}`} /><Loading /></>;
  const plan = payPlan({ c, ...s });
  const close = async (payments) => {
    const { data } = await api.post(`/comandas/${c.id}/close`, { payments });
    setSale(data);
    setCart(cid, []);
    printDoc(session, "sale", `Comprovante · Venda #${data.number}`, { sale: data }, { quiet: true }).catch(() => toast.warning("Venda fechada, mas o comprovante não foi impresso"));
  };
  const partial = async () => {
    const { data } = await api.post(`/comandas/${c.id}/partial-payment`, { payments: [{ method: s.method, amount: plan.pay }], item_ids: plan.itemIds });
    toast.success(`Pagamento de ${brl(plan.pay)} registrado. Falta ${brl(data.remaining)}${plan.change ? ` · Troco ${brl(plan.change)}` : ""}`, { duration: 6000 });
    if (session.user.partial_receipt) setAsk(partialPayload(c, data, plan, s.method, session.user.name));
    if (s.mode === "dividir" && s.split === "iguais" && !plan.partial) s.setPeople((n) => Math.max(1, n - 1));
    s.setRecv(""); s.setShare(""); s.setSel([]);
    load();
  };
  const submit = async () => {
    setBusy(true);
    try {
      if (plan.closes) await close(c.remaining > 0 ? [{ method: s.method, amount: s.method === "dinheiro" ? plan.got : plan.pay }] : []);
      else await partial();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (sale) return <><MTopBar title="Fechamento" back="/m/pdv" /><Done sale={sale} /></>;
  const blocked = c.offline || c.status !== "open";
  const ready = c.remaining <= 0 ? activeItems(c).length > 0 : plan.got > 0 && plan.due > 0 && !plan.over;
  const btnLabel = !ready ? (plan.due > 0 ? "Digite o valor" : "Escolha o que pagar") : plan.closes ? "Fechar comanda" : "Registrar pagamento parcial";
  return (
    <>
      <MTopBar title="Realizar Fechamento" back={`/m/comanda/${id}`} />
      <ComandaBar c={c} />
      <MPage>
        {blocked ? <p className="rounded-xl bg-amber-100 p-3 text-sm text-amber-900" data-testid="m-close-blocked">{c.offline ? "A comanda ainda está guardada no celular. Feche quando a internet voltar." : "Esta comanda não está aberta."}</p> : (
          <>
            {pendingFor(cid).length > 0 && <p className="rounded-xl bg-red-100 p-3 text-sm text-red-800">Há pedidos guardados no celular que ainda não foram enviados. <Link to="/m/backup" className="font-semibold underline">Ver</Link></p>}
            <Totals c={c} hideItems={s.mode === "itens"} />
            <DiscountBox c={c} reload={load} />
            {c.remaining > 0 && (<>
              <ModeBar mode={s.mode} setMode={s.setMode} />
              {s.mode === "dividir" && <SplitBox {...s} due={plan.due} />}
              {s.mode === "itens" && <ItemsPick c={c} sel={s.sel} setSel={s.setSel} />}
              <MethodGrid methods={s.methods} method={s.method} setMethod={s.setMethod} />
              <AmountBox recv={s.recv} setRecv={s.setRecv} plan={plan} method={s.method} />
            </>)}
          </>
        )}
      </MPage>
      {!blocked && (
        <Footer testId="m-pay-footer">
          <button onClick={submit} disabled={!ready || busy} className={cn("flex w-full items-center justify-between px-5 py-4 text-white disabled:opacity-60", plan.closes || !ready ? "bg-[#16a34a] hover:bg-[#15803d]" : "bg-amber-600 hover:bg-amber-700")} data-testid="m-pay-btn">
            <span className="flex items-center gap-2 font-display text-lg font-semibold">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Receipt className="h-5 w-5" />}{btnLabel}</span>
            <span className="font-mono text-xl font-bold">{brl(c.remaining > 0 ? plan.pay || plan.due : 0)}</span>
          </button>
        </Footer>
      )}
      <PrintAsk ask={ask} onClose={() => setAsk(null)} />
    </>
  );
}
